import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import type { InferGetServerSidePropsType, GetServerSideProps } from 'next'

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  Box,
  Breadcrumbs,
  IconButton,
  Button,
  Container,
  Divider,
  Grid,
  Link,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { getEventUrlFromTitleDate, moment } from "../../src/utils/date";
import Head from "next/head";

import { Favorite, People, Chat, AccountCircle } from "@mui/icons-material";
import { getStore } from "../../src/firebase"
import { getVoteDoc } from "../../src/services/voting";
import { useAuth } from "../../src/contexts/AuthProvider";

import { routes } from "../../src/routes";

import BuyTicketDialog from "../../src/components/BuyTicketDialog/BuyTicketDialog";
import { getTitleAndDateForEvent, getDateTime } from "../../src/utils/date";

import { CANONICAL_URL, IMG_PLACEHOLDER, POUND_SYMBOL } from "../../src/constants";
import { COLORS } from "../../src/theme";
import { ArrowRightAlt } from "@mui/icons-material";

import maleSVG from "../../public/images/male.svg";
import femaleSVG from "../../public/images/female.svg";
import Image from "next/image";
import { getRemainingTicketsText } from "../../src/utils/eventCard";
import { getUserByEmail } from "../../src/services/user";
import { getUserMatches } from "../../src/services/match";
import DraftAsHtml from "../../src/components/DraftAsHtml";
import { getAllEvents } from "../../src/services/event";
import type { Event, EventDoc } from '../../src/services/event';
import { getRedirects } from "../../src/services/config";
import { EventTypeDoc, getEventTypeById, serializeEventType, unserializeEventType } from "../../src/services/eventType";

type EventDetailsProps = {
  event: Event,
  serializedEventType?: EventTypeDoc
}

export const getServerSideProps: GetServerSideProps<EventDetailsProps> = async ({ params, req  }) => {
  if (!params.slug) {
    throw new Error('Slug missing for event page');
  }
  try {
    const event = await fetchEvent(params.slug);
    if (event.eventTypeId) {
      const eventType = await getEventTypeById(event.eventTypeId);
      return {
        props: {
          event,
          serializedEventType: serializeEventType(eventType),
        },
      };
    }
    return {
      props: {
        event
      },
    };
  } catch {
    const { date, title } = getTitleAndDateForEvent(req.url);
    const redirects = await getRedirects();
    const matchingRedirect = redirects.rules.findLast((rule) => {
      const regex = new RegExp(rule.match);
      return regex.test(req.url); // Return boolean result of test
    });
    return {
      redirect: {
        destination: matchingRedirect?.to ?? routes.events,
        permanent: true,
      },
    }
  } 
} 



let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PK);
  }
  return stripePromise;
};

type Match = {
  id: string;
  matchType: number, 
  users: string[]
}

const fetchEvent = async (slug): Promise<Event> => {
  const { db, getDocs, query, collection, where } = await getStore();

  if (!slug) {
    throw new Error('No slug provided')
  }

  const { date, title } = getTitleAndDateForEvent(slug);

  const q = query(
    collection(db, "events"),
    where("lowercaseTitle", "==", title)
  );
  const querySnapshot = await getDocs(q);

  if (querySnapshot.docs.length == 0) {
    throw new Error(`No event with title ${title} found`)
  }

  const doc = querySnapshot.docs.find((doc) => {
    const eventData = doc.data();
    const formattedDate = moment(eventData.dateTime.toDate()).format(
      "D MMM YYYY"
    );
    if (date == formattedDate) {
      return true
    }
  });
  if (!doc) {
    throw new Error(`No event with date ${date} found`)
  }
  const data = doc.data() as EventDoc;
  return ({
    ...data,
    id: doc.id,
    dateTime: getDateTime(data.dateTime).toISOString(),
    createdAt: getDateTime(data.createdAt).toISOString(),
  })
};



export default function EventDetails(props: EventDetailsProps) {
  const { event: serialisedEvent, serializedEventType } = props;
  const event = {
    ...serialisedEvent,
    dateTime: getDateTime(serialisedEvent.dateTime),
    createdAt: getDateTime(serialisedEvent.createdAt),
  }
  // console.log(event);
  const router = useRouter();
  const eventType = serializedEventType ? unserializeEventType(serializedEventType) : null;
  // const slug = router.query.slug;

  const { user } = useAuth();
  const [votesSyncing, setVotesSyncing] = useState(true);
  const [open, setOpen] = useState(false);
  const [nrOfTickets, setNrOfTickets] = useState(1);
  const [alreadyVoted, setAlreadyVoted] = useState(false);
  const [votes, setVotes] = useState([]);
  // const [matches, setMatches] = useState<Match[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));


  let canonicalEventUrl = '';
  if (event.title && event.dateTime) {
    canonicalEventUrl = `${CANONICAL_URL}${routes.events}/${getEventUrlFromTitleDate(event.title, event.dateTime)}`;
  }

  const imageUrlForCard = useMemo(() => {
    if (isMobile && event.imgMobileUrl) {
      return event.imgMobileUrl;
    }

    if (event.imgURL) {
      return event.imgURL;
    }

    return IMG_PLACEHOLDER;
  }, [isMobile, event.id]);

  const nrOfAvailableTickets = useMemo(() => {
    const { menAvailableTickets = 0, womenAvailableTickets = 0 } = event;
    const totalNrOfTickets =
      Number(menAvailableTickets) + Number(womenAvailableTickets);
    if (user) {
      if (user.gender === "M") {
        return Math.min(Number(menAvailableTickets), 5);
      } else {
        return Math.min(Number(womenAvailableTickets), 5);
      }
    }

    // fallback
    if (totalNrOfTickets > 5) {
      return 5;
    }

    return totalNrOfTickets;
  }, [event.id, user]);

  const isVoteEnded = useMemo(() => {
    if (event.votingPeriodEnded) return true;
    if (alreadyVoted) return true;
    return false;
  }, [alreadyVoted, event.id]);

  const fetchVoting = async () => {
    setVotesSyncing(true);
    const voteDocs = await getVoteDoc(event.id, user.email);
    const matches = await getUserMatches(user.email);

    // console.log(matches);
    // setMatches(matches);
    const voteData = [];
    if (voteDocs?.length) {
      setAlreadyVoted(true);
      for (const voteDoc of voteDocs) {
        const votes = voteDoc.data().votes;
        for (const vote of votes) {
          if (vote.value == 1 || vote.value == 2) {
            const targetVoteDocs = await getVoteDoc(event.id, vote.user);
            
            for (const targetVoteDoc of targetVoteDocs) {
              const relVotes = targetVoteDoc.data().votes;
              const relToUser = relVotes.find(
                (vote) =>
                  vote.user === user.email &&
                  (vote.value === 1 || vote.value === 2)
              );

              if (relToUser) {
                const matchUser = await getUserByEmail(vote.user);
                voteData.push({
                  value: Math.max(relToUser.value, vote.value),
                  user: matchUser,
                });
                break;
              }
            }
          }
        }
      }
      setVotes(voteData);
    }
    setVotesSyncing(false);
  };

  useEffect(() => {
    if (event.id && user && user.email) {
      fetchVoting();
    }
    if (typeof klaviyo !== 'undefined' && event.id && user && user.email) {
      klaviyo.track("Viewed Product",
        {
          "ProductName": event.title,
          "ProductID": event.id,
          "SKU": event.id,
          "Categories": [event.category],
          "ImageURL": event.imgURL,
          "URL": canonicalEventUrl,
          "Brand": "True Dating",
          "Price": event.ticketPrice,
          "CompareAtPrice": event.ticketPriceWithoutDiscount
        }
      );
    }
  }, [event.id, user?.email]); 


  return (
    <Elements stripe={getStripe()}>
      <Head>
        <title>{event.title} | True Dating</title>
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalEventUrl} key="canonical" />
      </Head>
      <Grid>
        <Container>
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{
              backgroundColor: COLORS.lightGrey,
              color: COLORS.shark,
              paddingTop: "12px",
              marginBottom: "-24px",
              "& a": {
                color: "grey",
              },
            }}
          >
            <Link underline="hover" color="inherit" href={routes.home}>
              Home
            </Link>
            <Link underline="hover" color="inherit" href={routes.events}>
              Events
            </Link>
            {eventType && (
              <Link
                underline="hover"
                color="inherit"
                href={`/${eventType.slug}`}
              >
                {eventType.name}
              </Link>
            )}
            <Typography color="text.primary">{event.title} {moment(event.dateTime).format('Do MMM')}</Typography>
          </Breadcrumbs>
        </Container>
        <Box
          sx={(theme) => ({
            padding: theme.spacing(6, 0),
            backgroundColor: COLORS.lightGrey,
            minHeight: "100vh",
          })}
        >
          <Container>
            <Grid container spacing={4}>
              <Grid
                item
                xs={12}
                md={7}
                sx={{
                  position: "relative",
                  "& #event-img": {
                    position: "sticky",
                    top: "75px",
                  },
                }}
              >
                <Box
                  id="event-img"
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    maxHeight: 500,
                    minHeight: 400,
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[2],
                    [theme.breakpoints.down("xs")]: {
                      minHeight: 220,
                    },
                    [theme.breakpoints.down("sm")]: {
                      minHeight: 240,
                    },
                  }}
                >
                  <Image
                    style={{
                      borderRadius: theme.shape.borderRadius,
                      boxShadow: theme.shadows[2],
                      objectFit: "cover",
                      height: "100%",
                      width: "100%",
                    }}
                    fill
                    alt={"Venue Image"}
                    src={imageUrlForCard}
                    priority
                    unoptimized
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                {event.showBanner && event.banner && (
                  <Box display="flex" justifyContent="flex-end">
                    <Box
                      sx={{
                        backgroundColor: COLORS.radicalRed,
                        borderTopLeftRadius: theme.shape.borderRadius,
                        borderTopRightRadius: theme.shape.borderRadius,
                        borderBottomLeftRadius: theme.shape.borderRadius,
                        padding: theme.spacing(1),
                        marginBottom: theme.spacing(1),
                        maxWidth: "20rem",
                        position: "relative",
                      }}
                    >
                      <Typography color={"white"}>{event.banner}</Typography>
                    </Box>
                  </Box>
                )}
                <Typography
                  variant="h1"
                  fontSize="1.5rem"
                  fontWeight="fontWeightBold"
                  sx={{textAlign: "left"}}
                >
                  {event.title}
                </Typography>

{/**************************************************************
 * EVENT VOTING OPEN
 ***************************************************************/}


                {!isVoteEnded ? (
                  <>
                    <Typography variant="h6" component="div">
                      <Box fontWeight="fontWeightMedium">{event.venue}</Box>
                    </Typography>
                    <Grid container alignItems="center" spacing={2}
                      sx={{
                        marginTop: theme.spacing(1),
                      }}
                    >
                      <Grid item>
                        <Button
                          disabled={
                            !nrOfAvailableTickets ||
                            event.archived ||
                            event.dateTime < Date.now()
                          }
                          fullWidth
                          variant="contained"
                          color="primary"
                          endIcon={<ArrowRightAlt />}
                          onClick={(_) => {
                            if (!nrOfAvailableTickets) {
                              return;
                            }
                            return user
                              ? setOpen(true)
                              : router.push(
                                  {
                                    pathname: routes.login,
                                    query: { data: window.location.pathname },
                                  },
                                  routes.login
                                );
                          }}
                        >
                          {`Buy Ticket${nrOfTickets > 1 ? "s" : ""}`}
                        </Button>
                      </Grid>
                      <Grid item>
                        <TextField
                          disabled={
                            !nrOfAvailableTickets ||
                            event.dateTime < Date.now()
                          }
                          select
                          value={nrOfAvailableTickets ? nrOfTickets : ""}
                          variant="outlined"
                          size="small"
                          onChange={({ target: { value } }) =>
                            setNrOfTickets(Number(value))
                          }
                        >
                          {Array.from(Array(nrOfAvailableTickets).keys()).map(
                            (_, i) => (
                              <MenuItem key={`ticket_${i + 1}`} value={i + 1}>
                                {i + 1}
                              </MenuItem>
                            )
                          )}
                        </TextField>
                      </Grid>
                    </Grid>
                    <Box py={4} maxWidth={400}>
                      <Grid container>
                        <Grid
                          item
                          container
                          xs={6}
                          alignItems="center"
                          wrap="nowrap"
                        >
                          <Box maxWidth={24}>
                            <Image
                              src={maleSVG}
                              alt="Male Icon"
                              style={{ width: "100%" }}
                            />
                          </Box>
                          <Typography variant="body2" component="div">
                            <Box ml={2} fontWeight="fontWeightBold">
                              {getRemainingTicketsText(
                                event.menAvailableTickets
                              )}
                            </Box>
                          </Typography>
                        </Grid>
                        <Grid>
                          <Divider />
                        </Grid>
                        <Grid
                          item
                          container
                          xs={6}
                          alignItems="center"
                          wrap="nowrap"
                        >
                          <Box maxWidth={26}>
                            <Image
                              src={femaleSVG}
                              alt="Female Icon"
                              style={{ width: "100%" }}
                            />
                          </Box>
                          <Typography variant="body2" component="div">
                            <Box ml={2} fontWeight="fontWeightBold">
                              {getRemainingTicketsText(
                                event.womenAvailableTickets
                              )}
                            </Box>
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <Grid container alignItems="center">
                      <Grid item xs={8} md={6}>
                        <Box width={120}>
                          <strong>Age Range</strong>
                        </Box>
                        <Box>
                          {event.ageRangeFrom}{event.ageRangeTo ? ` - ${event.ageRangeTo}` : '+'}
                        </Box>
                      </Grid>
                      <Grid item xs={4} md={6}>
                        <Box width={120}>
                          <strong>Price</strong>
                        </Box>
                        <Box>
                          {event.ticketPriceWithoutDiscount && (
                            <>
                              <Typography
                                sx={{
                                  color: "primary.main",
                                  textDecoration: "line-through",
                                  textDecorationThickness: "1px",
                                  marginRight: "4px",
                                }}
                              >
                                {POUND_SYMBOL}
                                {event.ticketPriceWithoutDiscount}
                              </Typography>
                            </>
                          )}
                          {POUND_SYMBOL}
                          {event.ticketPrice}
                        </Box>
                      </Grid>
                      <Box py={2} width="100%">
                        <Divider />
                      </Box>
                    </Grid>
                    <Grid container>
                      <Grid item xs={8} md={6}>
                        <Box width={50}>
                          <strong>Date</strong>
                        </Box>
                        <Box>
                          {event.dateTime
                            ? moment(event.dateTime).format("dddd, Do MMM YYYY")
                            : "-"}
                        </Box>
                      </Grid>
                      <Grid item xs={4} md={6}>
                        <Box width={120}>
                          <strong>Time</strong>
                        </Box>
                        <Box>
                          {event.dateTime
                            ? event.dateTime.toLocaleTimeString(
                                "en-GB",
                                {
                                  timeStyle: "short",
                                  hour12: true,
                                }
                              )
                            : "-"}
                        </Box>
                      </Grid>
                      <Box py={2} width="100%">
                        <Divider />
                      </Box>
                    </Grid>
                    <Grid>
                      <Box width={120}>
                        <strong>Venue</strong>
                      </Box>
                      <Box color={COLORS.radicalRed}>{event.venue}</Box>
                      <Box py={2} width="100%">
                        <Divider />
                      </Box>
                    </Grid>
                    {event.venueAddress && (
                      <Grid>
                        <Box width={120}>
                          <strong>Venue Address</strong>
                        </Box>
                        <Box
                          sx={(theme) => ({
                            width: "calc(100% - 120px)",
                            [theme.breakpoints.down("sm")]: {
                              width: "auto",
                            },
                          })}
                        >
                          {event.venueAddress}
                        </Box>
                        <Box py={2} width="100%">
                          <Divider />
                        </Box>
                      </Grid>
                    )}
                    {event.venueDescription && (
                      <Grid>
                        <Box width={120}>
                          <strong>Venue Details</strong>
                        </Box>
                        <Box
                          sx={(theme) => ({
                            width: "calc(100% - 120px)",
                            [theme.breakpoints.down("sm")]: {
                              width: "auto",
                            },
                          })}
                        >
                          {event.venueDescription &&
                          <DraftAsHtml
                            raw={JSON.parse(event.venueDescription)}
                          />
              }
                        </Box>
                        <Box py={2} width="100%">
                          <Divider />
                        </Box>
                      </Grid>
                    )}
                    <Box pb={2}>
                      {event.description &&
                      <DraftAsHtml
                        raw={JSON.parse(event.description)}
                      />
    }
                    </Box>
                  </>
                ) 
                



/**************************************************************
 * EVENT VOTING CLOSED
 ***************************************************************/
                : (
                  <>
                    {!votesSyncing && votes.length == 0 && (
                      <Typography component="div">
                        <Box
                          py={2}
                          style={{ color: "#DD0713", fontWeight: 500 }}
                        >
                          Unfortunately you do not have any matches from this
                          event.
                        </Box>
                      </Typography>
                    )}
                    {!votesSyncing &&
                      votes.map((vote) => (
                        <Box
                          mb={5}
                          display="flex"
                          alignItems="center"
                          key={vote.user.email}
                          sx={(theme) => ({
                            backgroundColor: COLORS.lightGrey,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: theme.shape.borderRadius,
                            marginBottom: theme.spacing(2),
                            padding: theme.spacing(2),
                          })}
                        >
                          <Box
                            sx={(theme) => ({
                              minWidth: 80,
                              width: 80,
                              height: 80,
                              borderRadius: 50,
                              boxShadow: theme.shadows[2],
                              marginRight: theme.spacing(2),
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              backgroundRepeat: "no-repeat",
                              backgroundImage: `url(${ vote.user.photoURL })`,
                            })}
                          >
                            {!vote.user.photoURL && (<AccountCircle style={{width: '100%', height: '100%', opacity: 0.1}} />)}
                          </Box>
                          <Typography
                            variant="h6"
                            sx={{
                              width: "100%",
                              maxWidth: 200,
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {vote.user.firstName}
                          </Typography>
                          <Box>
                            {vote.value === 1 ? (
                              <Favorite color="primary" />
                            ) : (
                              <People style={{ fill: COLORS.green }} />
                            )}
                          </Box>
                          <Box ml="auto">
                            <IconButton
                              onClick={() =>
                                router.push(
                                  {
                                    pathname: routes.messages,
                                    query: { data: JSON.stringify(vote.user) },
                                  },
                                  routes.messages
                                )
                              }
                            >
                              <Chat color="primary" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                  </>
                )}
              </Grid>
            </Grid>
          </Container>
        </Box>
        <Box>
          <Container>
            <Box textAlign="center" pb={3} pt={5}>
              <Typography variant="h4">How It Works</Typography>
            </Box>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6} md={4}>
                <Box
                  sx={(theme) => ({
                    width: "100%",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    height: "100%",
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[2],
                    [theme.breakpoints.down("xs")]: {
                      minHeight: 220,
                    },
                    maxHeight: 300,
                    minHeight: 250,
                    backgroundImage: `url(${
                      event.howItWorks?.col1?.imgURL || IMG_PLACEHOLDER
                    })`,
                  })}
                />
                <Box pb={4} pt={3}>
                  {event.howItWorks?.col1?.description && <DraftAsHtml raw={JSON.parse(event.howItWorks?.col1?.description)}/>}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box
                  sx={(theme) => ({
                    width: "100%",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    height: "100%",
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[2],
                    [theme.breakpoints.down("xs")]: {
                      minHeight: 220,
                    },
                    maxHeight: 300,
                    minHeight: 250,
                    backgroundImage: `url(${
                      event.howItWorks?.col2?.imgURL || IMG_PLACEHOLDER
                    })`,
                  })}
                />
                <Box pb={4} pt={3}>
                  {event.howItWorks?.col2?.description && <DraftAsHtml raw={JSON.parse(event.howItWorks?.col2?.description)} />}
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={(theme) => ({
                    width: "100%",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    height: "100%",
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[2],
                    [theme.breakpoints.down("xs")]: {
                      minHeight: 220,
                    },
                    maxHeight: 300,
                    minHeight: 250,
                    backgroundImage: `url(${
                      event.howItWorks?.col3?.imgURL || IMG_PLACEHOLDER
                    })`,
                  })}
                />
                <Box pb={4} pt={3}>

                  {event.howItWorks?.col3?.description && <DraftAsHtml
                    raw={JSON.parse(
                      event.howItWorks?.col3?.description
                    )}
                  />}
                </Box>
              </Grid>
              <Grid item container justifyContent="center" xs={12}>
                <Box py={4} maxWidth={800}>
                  {event.howItWorks?.finalNotes && <DraftAsHtml
                    raw={JSON.parse(event.howItWorks?.finalNotes)}
                  />}
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
        {open && !event.designMyNightId && (
          <BuyTicketDialog
            open={true}
            onClose={() => setOpen(false)}
            nrOfTickets={nrOfTickets}
            event={event}
          />
        )}
      </Grid>
      <script
        type="application/ld+json"
        key="eventJsonLd"
        dangerouslySetInnerHTML={{
          // we use the @graph key to workaround a bug in safari
          __html: `{"@context": "http://schema.org", "@graph": ${JSON.stringify(
          [{
            "@context": "https://schema.org/", 
            "@type": "BreadcrumbList", 
            "itemListElement": [{
              "@type": "ListItem", 
              "position": 1, 
              "name": "Homepage",
              "item": "https://truedating.co.uk/"  
            },{
              "@type": "ListItem", 
              "position": 2, 
              "name": "Find Your Perfect Event",
              "item": "https://truedating.co.uk/events"  
            },{
              "@type": "ListItem", 
              "position": 3, 
              "name": event.title,
              "item": canonicalEventUrl
            }]
          }, 
          {
            "@context": "https://schema.org",
            "@type": "Event",
            "name": event.title,
            "startDate": event.dateTime,
            "endDate": new Date(event.dateTime.getTime() + (2*60*60*1000)),
            "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
            "eventStatus": "https://schema.org/EventScheduled",
            "location": {
              "@type": "Place",
              "name": event.venue,
              "address": {
                "@type": "PostalAddress",
                "streetAddress": event.venueAddress,
                "addressLocality": "",
                "postalCode": "",
                "addressCountry": "UK"
            
              }
            },
            "image": imageUrlForCard,
            "description": JSON.parse(event.description)?.blocks?.map(b => b.text).join(' '),
            "offers": {
              "@type": "Offer",
              "url": canonicalEventUrl,
              "price": event.ticketPrice,
              "priceCurrency": "GBP",
              "availability": "https://schema.org/InStock",
              "validFrom": event.dateTime
            },
            "performer": {
              "@type": "Person",
              "name": "True Dating"
            }
            }
          ])} }`
        }}
      />
    </Elements>
  );
}
